import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

/**
 * A document in the org context library — the shared knowledge base of
 * reference material (specs, designs, runbooks, CSVs, links, pasted text) that
 * agents read while planning and testing. This is the canonical client shape
 * (the exploratory feature re-exports it).
 */
export type ContextDocument = {
  id: string;
  fileName: string;
  fileType: string;
  /** Human-authored note on what this doc is / how an agent should use it. */
  description: string | null;
  fileSizeBytes: number | null;
  sourceUrl: string | null;
  status: string;
  createdAt: string;
};

export const CONTEXT_LIBRARY_KEY = ['context-library'] as const;

/**
 * List the whole org context library (the unified shared pool). Polls every 4s
 * while any document is still extracting (`pending`/`processing`) so freshly
 * uploaded files flip to `ready` without a manual refresh, then goes quiet.
 */
export function useContextDocuments() {
  return useQuery({
    queryKey: CONTEXT_LIBRARY_KEY,
    refetchInterval: (query) => {
      const data = query.state.data as ContextDocument[] | undefined;
      if (!data) return false;
      const extracting = data.some((d) => d.status === 'pending' || d.status === 'processing');
      return extracting ? 4_000 : false;
    },
    queryFn: async (): Promise<ContextDocument[]> => {
      const response = await fetch(`${API_URL}/context-documents`, { credentials: 'include' });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to load context library');
      }
      const json = (await response.json()) as { ok: true; data: ContextDocument[] };
      return json.data;
    },
  });
}

/** Upload files to the org context library (conversation-less). */
export function useUploadContextDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]): Promise<ContextDocument[]> => {
      const form = new FormData();
      for (const file of files) form.append('files', file);

      const response = await fetch(`${API_URL}/context-documents`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to upload context document');
      }

      const json = (await response.json()) as { ok: true; data: ContextDocument[] };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTEXT_LIBRARY_KEY });
    },
  });
}

/** Add a URL document to the org context library. */
export function useAddContextLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { url: string; description?: string }): Promise<ContextDocument> => {
      const response = await fetch(`${API_URL}/context-documents/link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to add context link');
      }

      const json = (await response.json()) as { ok: true; data: ContextDocument };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTEXT_LIBRARY_KEY });
    },
  });
}

/** Store pasted text as a context document. */
export function useAddContextPaste() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      content: string;
      label?: string;
      description?: string;
    }): Promise<ContextDocument> => {
      const response = await fetch(`${API_URL}/context-documents/paste`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to save pasted text');
      }

      const json = (await response.json()) as { ok: true; data: ContextDocument };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTEXT_LIBRARY_KEY });
    },
  });
}

/** Edit a document's description and/or rename it. */
export function useUpdateContextDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      description?: string | null;
      fileName?: string;
    }): Promise<ContextDocument> => {
      const { id, ...body } = input;
      const response = await fetch(`${API_URL}/context-documents/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to update document');
      }

      const json = (await response.json()) as { ok: true; data: ContextDocument };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTEXT_LIBRARY_KEY });
    },
  });
}

/** Permanently delete a document from the library (removes the S3 file too). */
export function useDeleteContextDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await fetch(`${API_URL}/context-documents/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to delete document');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTEXT_LIBRARY_KEY });
    },
  });
}

/**
 * Resolve a document's download URL (a presigned S3 URL, or the source URL for
 * links) and open it in a new tab. Returns a callback, not a mutation — the
 * fetch is a one-shot redirect, not cache-mutating.
 */
export function useDownloadContextDocument() {
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const response = await fetch(`${API_URL}/context-documents/${id}/download`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to get download URL');
      }
      const json = (await response.json()) as { ok: true; url: string };
      return json.url;
    },
  });
}
